import React, { useState } from 'react';
import { ArrowLeft, Lock, CheckCircle2, Clock, XCircle } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';
import ExpressInterestModal from './ExpressInterestModal';

const TIMELINE_LABELS = {
  '6_12_months':  '6–12 Months',
  '1_2_years':    '1–2 Years',
  '3_plus_years': '3+ Years',
};

const INDUSTRY_LABELS = {
  trades_construction:   'Trades & Construction',
  manufacturing:         'Manufacturing',
  professional_services: 'Professional Services',
  retail:                'Retail',
  technology:            'Technology',
  hvac:                  'HVAC',
  restaurant:            'Restaurant',
  trades:                'Trades',
  health:                'Health',
  auto:                  'Auto',
  saas:                  'SaaS',
  other:                 'Other',
};

const BUCKET_LABELS = {
  under_500k:    'Under $500K',
  '500k_to_1m':  '$500K – $1M',
  '1m_to_5m':    '$1M – $5M',
  '5m_plus':     '$5M+',
};

const formatCurrency = (value) => {
  if (value == null) return 'Make Offer';
  const num = Number(value);
  if (Number.isNaN(num) || num === 0) return 'Make Offer';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
};

export default function MarketplaceListingPage({
  listing,
  loading = false,
  buyerProfile = null,
  persona = 'BUYER',
  userName = '',
  userInitials = '',
  planProgress = { completed: 0, total: 5, percentage: 0 },
  actionPlanBadge = 0,
  expressing = false,
  expressErrors = [],
  onExpressInterest, // ({ interestMessage, budgetReadiness, timelineAlignment }) => Promise<{ ok: boolean }>
  onSignNda,         // () => Promise
}) {
  const layoutProps = { persona, userName, userInitials, planProgress, actionPlanBadge };
  const [modalOpen, setModalOpen] = useState(false);

  if (loading) {
    return (
      <AppLayout {...layoutProps}>
        <div className="max-w-5xl mx-auto p-8">
          <p className="text-sm text-muted-foreground">Loading listing…</p>
        </div>
      </AppLayout>
    );
  }
  if (!listing) {
    return (
      <AppLayout {...layoutProps}>
        <div className="max-w-5xl mx-auto p-8">
          <p className="text-sm text-muted-foreground">Listing not found.</p>
        </div>
      </AppLayout>
    );
  }

  const eoi          = listing.eoi_state || 'no_eoi';
  const ndaActive    = Boolean(listing.nda_active);
  const industry     = INDUSTRY_LABELS[listing.industry] || listing.industry || '—';
  const timeline     = TIMELINE_LABELS[listing.timeline] || listing.timeline || '—';
  const dealPrefs    = listing.deal_preferences || [];
  const photos       = listing.photo_urls || [];
  const placeholderPhotoCount = Math.max(4, photos.length);

  const isUnderLoi      = listing.deal_state === 'under_loi';
  const isUnderContract = listing.deal_state === 'under_contract';
  const isClosedSold    = listing.deal_state === 'closed_sold';
  const isClosedWithdr  = listing.deal_state === 'closed_withdrawn';
  const isOffMarket     = isUnderLoi || isUnderContract || isClosedSold || isClosedWithdr;
  const offMarketLabel  =
    isUnderLoi      ? 'LISTING UNDER LOI'        :
    isUnderContract ? 'LISTING UNDER CONTRACT'   :
    isClosedSold    ? 'LISTING CLOSED · SOLD'    :
    isClosedWithdr  ? 'LISTING CLOSED · WITHDRAWN' : null;
  const offMarketBlurb  =
    isUnderLoi
      ? "The seller has accepted an LOI from another buyer. This listing is no longer accepting new inquiries — if the deal falls through, it may return to the marketplace."
      : isUnderContract
        ? "A binding contract is in place. This listing is no longer accepting new inquiries."
        : "The seller has closed this listing. New inquiries are no longer accepted.";

  return (
    <AppLayout {...layoutProps}>
      <div className="max-w-5xl mx-auto p-8 pb-32">
        <a
          href="/succeedo/marketplace"
          className="inline-flex items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Marketplace
        </a>

        <header className="mb-8">
          <h1 className="font-serif text-[42px] font-light tracking-[-0.02em] text-foreground mb-2">
            {ndaActive ? (listing.revealed_business_name || industry) : industry}
          </h1>
          <div className="flex items-center gap-3">
            <span className="text-[16px] text-muted-foreground">{listing.province || '—'}</span>
            <span className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-2 py-1 rounded">
              Confidential Listing
            </span>
          </div>
        </header>

        {/* Off-market notice — buyers see a clear "no longer accepting
            inquiries" banner when the seller has moved the listing
            past published. The Express Interest CTA is also suppressed
            below for these states. */}
        {isOffMarket && (
          <div className="bg-muted/40 border-l-4 border-l-[hsl(var(--succeedo-orange))] border border-border rounded-[16px] p-5 mb-8">
            <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--succeedo-orange))] mb-2">
              {offMarketLabel}
            </div>
            <p className="text-[13px] text-foreground">{offMarketBlurb}</p>
          </div>
        )}

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Annual Revenue" value={ndaActive ? formatCurrency(listing.revenue) : (BUCKET_LABELS[listing.revenue_range_bucket] || '—')} />
          <Stat label="Adjusted EBITDA" value={ndaActive ? formatCurrency(listing.adjusted_ebitda) : '—'} />
          <Stat label="Asking Price" value={formatCurrency(listing.asking_price)} />
          <Stat label="Employees" value={ndaActive ? (listing.employees ?? '—') : (BUCKET_LABELS[listing.employee_count_bucket] || '—')} />
        </section>

        <section className="bg-card border border-border rounded-[16px] p-6 mb-8">
          <h2 className="font-serif text-[22px] font-light text-foreground mb-4">Listing Details</h2>
          <dl className="space-y-4">
            <DetailRow label="Years in Operation" value={`${listing.years_in_operation ?? '—'} years`} bordered />
            <DetailRow
              label="Deal Preferences"
              valueNode={(
                <div className="flex flex-wrap gap-2 justify-end">
                  {dealPrefs.length === 0 ? (
                    <span className="text-[14px] text-muted-foreground">—</span>
                  ) : dealPrefs.map((pref) => (
                    <span key={pref} className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-[11px]">{pref}</span>
                  ))}
                </div>
              )}
              bordered
            />
            <DetailRow
              label="Timeline"
              valueNode={(
                <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-[11px]">{timeline}</span>
              )}
            />
          </dl>
        </section>

        {listing.business_story && (
          <section className="bg-card border border-border rounded-[16px] p-6 mb-8">
            <h2 className="font-serif text-[22px] font-light text-foreground mb-4">About the Business</h2>
            <p className="text-[14px] text-foreground leading-relaxed whitespace-pre-line">
              {listing.business_story}
            </p>
            {!ndaActive && (
              <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
                <Lock className="w-3 h-3" />
                <span>Full details visible after NDA</span>
              </div>
            )}
          </section>
        )}

        {listing.key_strengths && (
          <section className="bg-card border border-border rounded-[16px] p-6 mb-8">
            <h2 className="font-serif text-[22px] font-light text-foreground mb-4">Key Strengths & Opportunities</h2>
            <div className="text-[14px] text-foreground leading-relaxed whitespace-pre-line">
              {listing.key_strengths}
            </div>
          </section>
        )}

        <section className="bg-card border border-border rounded-[16px] p-6 mb-8">
          <h2 className="font-serif text-[22px] font-light text-foreground mb-4">Photos</h2>
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: placeholderPhotoCount }).map((_, i) => {
              const url = photos[i];
              if (ndaActive && url) {
                return <img key={i} src={url} alt={`Listing ${i + 1}`} className="aspect-video object-cover rounded-lg" />;
              }
              return (
                <div key={i} className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                  <div className="absolute inset-0 backdrop-blur-xl bg-muted/80 flex items-center justify-center">
                    <div className="text-center">
                      <Lock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <div className="text-[12px] text-muted-foreground">Photos visible after NDA</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {ndaActive && (
          <section className="bg-card border border-border rounded-[16px] p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="font-mono text-[11px] uppercase text-green-500 tracking-wide">
                NDA Active · Full Details Unlocked
              </span>
            </div>
            <dl className="space-y-4">
              <DetailRow label="Owner Name"   value={listing.revealed_owner_name || '—'} bordered />
              <DetailRow label="Business Name" value={listing.revealed_business_name || '—'} bordered />
              <DetailRow label="Address"       value={listing.revealed_address || '—'} />
            </dl>
          </section>
        )}

        {!ndaActive && (
          <div className="bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 rounded-[16px] p-4 mb-8">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-[hsl(var(--succeedo-orange))]" />
              <span className="font-mono text-[11px] uppercase text-[hsl(var(--succeedo-orange))] tracking-wide">
                Confidential Until NDA Signed
              </span>
            </div>
          </div>
        )}

        {/* Hide the EOI CTA when the listing is off-market. The
            off-market banner above tells the buyer why they can't act,
            and ExpressInterest would reject server-side anyway. */}
        {!isOffMarket && (
          <div className="sticky bottom-8">
            <CtaCard
              state={eoi}
              onExpressInterest={() => setModalOpen(true)}
              onSignNda={onSignNda}
            />
          </div>
        )}
      </div>

      <ExpressInterestModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={onExpressInterest}
        buyerProfile={buyerProfile}
        submitting={expressing}
        serverErrors={expressErrors}
      />
    </AppLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-[16px] p-4">
      <div className="font-mono text-[11px] uppercase text-muted-foreground mb-2 tracking-wide">{label}</div>
      <div className="text-[20px] font-semibold text-foreground">{value}</div>
    </div>
  );
}

function DetailRow({ label, value, valueNode, bordered }) {
  return (
    <div className={`flex items-center justify-between py-3 ${bordered ? 'border-b border-border' : ''}`}>
      <span className="text-[13px] text-muted-foreground">{label}</span>
      {valueNode || <span className="text-[14px] text-foreground font-medium">{value}</span>}
    </div>
  );
}

function CtaCard({ state, onExpressInterest, onSignNda }) {
  if (state === 'no_eoi') {
    return (
      <div className="bg-card border border-border rounded-[16px] p-6 shadow-2xl">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-serif text-[20px] font-light text-foreground mb-1">
              Interested in this listing?
            </h3>
            <p className="text-[13px] text-muted-foreground">
              Submit your interest to connect with the seller through a Succeedo broker.
            </p>
          </div>
          <button
            type="button"
            onClick={onExpressInterest}
            className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-[9px] text-sm hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors"
          >
            Express Interest
          </button>
        </div>
      </div>
    );
  }

  if (state === 'eoi_submitted') {
    return (
      <div className="bg-card border border-border rounded-[16px] p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-[hsl(var(--succeedo-orange))] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-serif text-[18px] font-light text-foreground mb-1">
              Your interest has been submitted
            </h3>
            <p className="text-[13px] text-muted-foreground">
              The seller will review within 24 hours.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'nda_presented') {
    return (
      <div className="bg-card border border-border rounded-[16px] p-6 shadow-2xl">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-serif text-[20px] font-light text-foreground mb-1">
              NDA ready to sign
            </h3>
            <p className="text-[13px] text-muted-foreground">
              The seller approved your interest. Sign the NDA blind — the seller will then countersign to unlock full business details.
            </p>
          </div>
          <button
            type="button"
            onClick={onSignNda}
            className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-[9px] text-sm hover:bg-[hsl(var(--succeedo-orange))]/90 transition-colors"
          >
            Sign NDA
          </button>
        </div>
      </div>
    );
  }

  if (state === 'nda_pending_seller') {
    return (
      <div className="bg-card border border-border rounded-[16px] p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-[hsl(var(--succeedo-orange))] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-serif text-[18px] font-light text-foreground mb-1">
              You've signed — awaiting seller's countersign
            </h3>
            <p className="text-[13px] text-muted-foreground">
              The seller is reviewing your signed NDA. Full details unlock once they countersign.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'nda_signed') {
    return (
      <div className="bg-card border border-border rounded-[16px] p-6 shadow-2xl">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-serif text-[20px] font-light text-foreground mb-1">
              Full details unlocked
            </h3>
            <p className="text-[13px] text-muted-foreground">
              You can now view all business information and contact the seller.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'declined') {
    return (
      <div className="bg-card border border-border rounded-[16px] p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <XCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          <div>
            <h3 className="font-serif text-[18px] font-light text-muted-foreground mb-1">
              Interest not approved
            </h3>
            <p className="text-[13px] text-muted-foreground">
              The seller is not pursuing this opportunity right now.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
